import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react'
import { CommandCenterShell } from '../../shell'
import { api, ApiError } from '../../../../api/client'
import './client-intake-workroom.css'

/**
 * ClientIntakeWorkroom — the perfected Client Intake capture form, now wired
 * to the real client store.
 *
 * Faithful React port of docs/design-references/client-intake.reference.html
 * (the design the operator built and perfected), rendered inside the shared
 * CommandCenterShell. The body owns three glass cards: New Client, Connections,
 * Recent Clients.
 *
 * REAL, not session-local: Client name / Project name / Contact email are
 * type-or-pick comboboxes sourced from GET /api/square/command-center/intake/
 * directory (the operator's actual saved sq_clients + sq_projects), so values
 * saved on other projects don't have to be re-typed. "Save Client" persists
 * through the audited, fail-closed atomic RPC (create-client-project): it
 * creates a bound client + project + sealed receipt, and the operator-facing
 * delivery stage + notes are attached to the project's metadata.
 *
 * Still honest about what is NOT wired: the Vercel / GitHub / Supabase
 * *provisioning* connections are not connected — nothing is auto-provisioned
 * until they are, and the UI says so.
 */

const HERO = '#0891b2'

interface RecentEntry {
  id: string
  clientName: string
  projectName: string
  stage: string | null
  createdAt: string | null
}

function IconUser() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="8" r="3.6" fill="none" stroke="currentColor" strokeWidth="1.5" />
      <path d="M5 20c0-3.6 3.1-6.2 7-6.2s7 2.6 7 6.2" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}
function IconLink() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M9.5 14.5l5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M8 16.5l-1.8 1.8a3.2 3.2 0 0 1-4.5-4.5L4 11.5M16 7.5l1.8-1.8a3.2 3.2 0 0 1 4.5 4.5L20.5 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}
function IconReceipt() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M6 3.5h12v17l-2-1.3-2 1.3-2-1.3-2 1.3-2-1.3-2 1.3z" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M8.5 8h7M8.5 11.5h7M8.5 15h4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}
function IconPlus() {
  return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /></svg>
}

const CONNECTIONS = ['Vercel', 'GitHub', 'Supabase'] as const
const PROJECT_TYPES = ['Mockup', 'MVP', 'Full Build'] as const

// ── accessible type-or-pick combobox ────────────────────────────────────────
interface ComboOption {
  value: string
  hint?: string
}

function CiCombobox({
  id, value, onChange, onPick, options, placeholder, error, onEnterSubmit, inputMode,
}: {
  id: string
  value: string
  onChange: (v: string) => void
  onPick?: (v: string) => void
  options: ComboOption[]
  placeholder?: string
  error?: boolean
  onEnterSubmit?: () => void
  inputMode?: 'text' | 'email'
}) {
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(-1)
  const rootRef = useRef<HTMLDivElement>(null)

  // Unique, prefix/substring-filtered, capped. If the only remaining option is
  // exactly what's already typed, offer nothing (no redundant one-item list).
  const filtered = useMemo(() => {
    const q = value.trim().toLowerCase()
    const seen = new Set<string>()
    const out: ComboOption[] = []
    for (const o of options) {
      const key = o.value.toLowerCase()
      if (!o.value || seen.has(key)) continue
      seen.add(key)
      if (!q || key.includes(q)) out.push(o)
      if (out.length >= 8) break
    }
    if (out.length === 1 && out[0].value.toLowerCase() === q) return []
    return out
  }, [options, value])

  const canOpen = open && filtered.length > 0

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false)
        setActive(-1)
      }
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  const pick = (v: string) => {
    onChange(v)
    onPick?.(v)
    setOpen(false)
    setActive(-1)
  }

  const onKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (!canOpen) { setOpen(true); return }
      setActive((i) => Math.min(i + 1, filtered.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      if (canOpen && active >= 0 && active < filtered.length) {
        e.preventDefault()
        pick(filtered[active].value)
      } else if (onEnterSubmit) {
        e.preventDefault()
        onEnterSubmit()
      }
    } else if (e.key === 'Escape') {
      if (open) { e.preventDefault(); setOpen(false); setActive(-1) }
    }
  }

  const listId = `${id}-list`
  return (
    <div className="ci-combo" ref={rootRef}>
      <input
        id={id}
        className="ci-input ci-combo-input"
        role="combobox"
        aria-expanded={canOpen}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={canOpen && active >= 0 ? `${id}-opt-${active}` : undefined}
        autoComplete="off"
        data-error={error || undefined}
        inputMode={inputMode}
        value={value}
        placeholder={placeholder}
        onChange={(e) => { onChange(e.target.value); setOpen(true); setActive(-1) }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />
      {canOpen && (
        <ul className="ci-combo-list" id={listId} role="listbox">
          {filtered.map((o, i) => (
            <li
              key={o.value}
              id={`${id}-opt-${i}`}
              role="option"
              aria-selected={i === active}
              className="ci-combo-option"
              data-active={i === active || undefined}
              onMouseDown={(e) => { e.preventDefault(); pick(o.value) }}
              onMouseEnter={() => setActive(i)}
            >
              <span className="ci-combo-option-value">{o.value}</span>
              {o.hint ? <span className="ci-combo-option-hint">{o.hint}</span> : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export interface ClientIntakeWorkroomProps {
  onHome?: () => void
  onBackCommandCenter?: () => void
  onBackOperations?: () => void
}

export default function ClientIntakeWorkroom({ onHome, onBackCommandCenter, onBackOperations }: ClientIntakeWorkroomProps) {
  const [name, setName] = useState('')
  const [projectName, setProjectName] = useState('')
  const [projectType, setProjectType] = useState<string>('Mockup')
  const [contact, setContact] = useState('')
  const [notes, setNotes] = useState('')
  const [nameError, setNameError] = useState(false)
  const [saving, setSaving] = useState(false)

  // The real reuse directory (the operator's saved clients + projects).
  const [dirClients, setDirClients] = useState<Array<{ id: string; name: string; email: string | null; phone: string | null }>>([])
  const [dirProjects, setDirProjects] = useState<Array<{ id: string; name: string; clientName: string | null; stage: string | null; createdAt: string | null }>>([])
  const [dirLoading, setDirLoading] = useState(true)
  const [dirError, setDirError] = useState(false)

  const [toast, setToast] = useState<string | null>(null)
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const showToast = useCallback((msg: string) => {
    setToast(msg)
    if (toastTimer.current) clearTimeout(toastTimer.current)
    toastTimer.current = setTimeout(() => setToast(null), 4200)
  }, [])

  const loadDirectory = useCallback(async () => {
    setDirLoading(true)
    try {
      const res = await api.intake.directory()
      setDirClients(res.clients || [])
      setDirProjects(res.projects || [])
      setDirError(false)
    } catch {
      // Honest degradation: suggestions unavailable, but the operator can
      // still type new values and save. Never fake a directory.
      setDirError(true)
    } finally {
      setDirLoading(false)
    }
  }, [])

  useEffect(() => { void loadDirectory() }, [loadDirectory])
  useEffect(() => () => { if (toastTimer.current) clearTimeout(toastTimer.current) }, [])

  // Dropdown option sets, derived from the real directory.
  const clientNameOptions = useMemo<ComboOption[]>(
    () => dirClients.filter((c) => c.name).map((c) => ({ value: c.name, hint: c.email ?? undefined })),
    [dirClients],
  )
  const emailOptions = useMemo<ComboOption[]>(
    () => dirClients.filter((c) => c.email).map((c) => ({ value: c.email as string, hint: c.name || undefined })),
    [dirClients],
  )
  const projectNameOptions = useMemo<ComboOption[]>(
    () => dirProjects.filter((p) => p.name).map((p) => ({ value: p.name, hint: p.clientName ?? undefined })),
    [dirProjects],
  )

  const recent = useMemo<RecentEntry[]>(
    () => dirProjects.slice(0, 8).map((p) => ({
      id: p.id,
      clientName: p.clientName || p.name,
      projectName: p.name,
      stage: p.stage,
      createdAt: p.createdAt,
    })),
    [dirProjects],
  )

  // Picking a saved value autofills its companions (only when empty — never
  // clobber what the operator already typed).
  const onPickClientName = (v: string) => {
    setName(v)
    const match = dirClients.find((c) => c.name.toLowerCase() === v.toLowerCase())
    if (match?.email && !contact.trim()) setContact(match.email)
  }
  const onPickEmail = (v: string) => {
    setContact(v)
    const match = dirClients.find((c) => (c.email ?? '').toLowerCase() === v.toLowerCase())
    if (match?.name && !name.trim()) setName(match.name)
  }
  const onPickProjectName = (v: string) => {
    setProjectName(v)
    const match = dirProjects.find((p) => p.name.toLowerCase() === v.toLowerCase())
    if (match?.clientName && !name.trim()) setName(match.clientName)
  }

  const saveClient = useCallback(async () => {
    if (saving) return
    const trimmed = name.trim()
    if (!trimmed) {
      setNameError(true)
      showToast('Add a client name first.')
      return
    }
    setNameError(false)
    const project = projectName.trim() || trimmed
    setSaving(true)
    try {
      const result = await api.intake.createClientProject({
        client_name: trimmed,
        project_name: project,
        contact_email: contact.trim() || undefined,
        project_type: 'other',
        stage: projectType,
        notes: notes.trim() || undefined,
      })
      const partial = result.metadata_persisted === false
      showToast(
        partial
          ? `Saved “${project}” — client + project created and bound. (Stage/notes couldn’t attach this time.)`
          : `Saved “${project}” — client and project created, bound, and receipted.`,
      )
      setName(''); setProjectName(''); setProjectType('Mockup'); setContact(''); setNotes('')
      await loadDirectory()
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'slug_taken') {
          showToast(`A project named “${project}” already exists — give this one a distinct project name.`)
        } else if (err.code === 'no_active_org') {
          showToast('No active organization is resolved for your account — can’t save a client yet.')
        } else if (err.code === 'forbidden') {
          showToast('Your account isn’t permitted to save clients (needs member access or above).')
        } else {
          showToast(`Couldn’t save: ${err.message || err.code}.`)
        }
      } else {
        showToast('Couldn’t save — check your connection and try again.')
      }
    } finally {
      setSaving(false)
    }
  }, [saving, name, projectName, projectType, contact, notes, showToast, loadDirectory])

  const contextBar = (
    <span className="ci-intro">
      Capture a client in seconds — pick a client, project, or email you’ve already saved, or type a new one.
      Saving creates a real, bound client + project record. Connect Vercel, GitHub, and Supabase below to
      auto-provision later; those connections aren’t wired yet.
    </span>
  )

  return (
    <CommandCenterShell
      onHome={onHome}
      onBackCommandCenter={onBackCommandCenter}
      parentBack={{ label: '← Operations', onClick: onBackOperations }}
      breadcrumb={['COMMAND CENTER', 'OPERATIONS', 'CLIENT INTAKE']}
      title="Client Intake"
      titleAccent={HERO}
      statusBadge={<span className="ci-status-pill">Setup Required</span>}
      honestyBadge={{
        label: 'LIVE · SAVES TO YOUR CLIENT STORE',
        title: 'Saving a client creates a real, bound client + project record (sq_clients + sq_projects) with a sealed receipt, and the dropdowns reuse your actual saved records. The Vercel, GitHub, and Supabase provisioning connections below are not wired yet — nothing is auto-provisioned until they are.',
      }}
      contextBar={contextBar}
      maxWidth={860}
    >
      <div className="sqv-surface sqv-atmosphere sqv-graph ci-root" data-sqv-surface="client-intake">
        {/* New Client */}
        <section className="ci-card ci-card--combo">
          <div className="ci-card-head"><IconUser /><span>New Client</span></div>

          {dirError ? (
            <p className="ci-dir-note">Couldn’t load your saved clients for suggestions — you can still type new ones and save.</p>
          ) : null}

          <div className="ci-form-row">
            <div className="ci-form-group">
              <label className="ci-field-label" htmlFor="ci-name">Client name</label>
              <CiCombobox
                id="ci-name"
                value={name}
                onChange={setName}
                onPick={onPickClientName}
                options={clientNameOptions}
                error={nameError}
                onEnterSubmit={() => void saveClient()}
                placeholder="e.g. Arbuckle Property Group"
              />
            </div>
            <div className="ci-form-group">
              <label className="ci-field-label" htmlFor="ci-project">Project name</label>
              <CiCombobox
                id="ci-project"
                value={projectName}
                onChange={setProjectName}
                onPick={onPickProjectName}
                options={projectNameOptions}
                placeholder="e.g. Arbuckle Manager Workspace — defaults to client name"
              />
            </div>
          </div>

          <div className="ci-form-row">
            <div className="ci-form-group">
              <label className="ci-field-label" htmlFor="ci-type">Project type</label>
              <select id="ci-type" className="ci-select" value={projectType} onChange={(e) => setProjectType(e.target.value)}>
                {PROJECT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="ci-form-group">
              <label className="ci-field-label" htmlFor="ci-contact">Contact email <span className="ci-optional">(optional)</span></label>
              <CiCombobox
                id="ci-contact"
                value={contact}
                onChange={setContact}
                onPick={onPickEmail}
                options={emailOptions}
                inputMode="email"
                placeholder="name@company.com"
              />
            </div>
          </div>

          <div className="ci-form-group">
            <label className="ci-field-label" htmlFor="ci-notes">Notes <span className="ci-optional">(optional)</span></label>
            <textarea id="ci-notes" className="ci-textarea" value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="Requirements, scope, anything to remember before the first call…" />
          </div>

          <button type="button" className="ci-btn-primary" onClick={() => void saveClient()} disabled={saving} aria-busy={saving}>
            <IconPlus />{saving ? 'Saving…' : 'Save Client'}
          </button>
          <p className="ci-field-hint">
            Clients and projects you save here are real records — reused as the suggestions above and available
            to Team Onboarding to assign people to.
          </p>
        </section>

        {/* Connections */}
        <section className="ci-card">
          <div className="ci-card-head"><IconLink /><span>Connections</span></div>
          {CONNECTIONS.map((service) => (
            <div className="ci-provision-row" key={service}>
              <span className="ci-provision-name">{service}</span>
              <span className="ci-provision-status">Not Connected</span>
              <button type="button" className="ci-btn-connect"
                onClick={() => showToast(`${service} connection isn’t wired yet — this is where OAuth setup will live.`)}>
                Connect
              </button>
            </div>
          ))}
          <p className="ci-caption">
            Connect once here — every saved client can then provision a project, repo, and database from these.
            None are connected yet.
          </p>
        </section>

        {/* Recent Clients */}
        <section className="ci-card">
          <div className="ci-card-head"><IconReceipt /><span>Recent Clients</span></div>
          {dirLoading ? (
            <p className="ci-empty">Loading your saved clients…</p>
          ) : recent.length === 0 ? (
            <p className="ci-empty">No clients yet — use the form above to add your first one.</p>
          ) : (
            recent.map((c) => (
              <div className="ci-client-entry" key={c.id}>
                <div className="ci-client-info">
                  <span className="ci-client-name">{c.clientName}</span>
                  <span className="ci-client-meta">
                    {c.projectName}{c.stage ? ` · ${c.stage}` : ''}{formatWhen(c.createdAt)}
                  </span>
                </div>
                <div className="ci-client-actions">
                  <button type="button" className="ci-btn-provision"
                    onClick={() => showToast('Provisioning needs the connections above — not set up yet.')}>
                    Provision project
                  </button>
                </div>
              </div>
            ))
          )}
          <p className="ci-caption">
            These are real clients saved to your store — reused as suggestions above and available to Team
            Onboarding. Provisioning a repo/database still needs the connections above.
          </p>
        </section>
      </div>

      <div className="ci-toast" data-show={toast !== null} role="status" aria-live="polite">{toast}</div>
    </CommandCenterShell>
  )
}

// Short "· added Jul 12" suffix; silent when the timestamp is missing.
function formatWhen(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return ` · added ${d.toLocaleDateString([], { month: 'short', day: 'numeric' })}`
}
